import { createReducer, createAction } from 'redux-act';
import { createSelector } from 'reselect';
import { selectCurrentId } from '@gandi/redux-sharing';
import { createResource } from '@gandi/resource/redux';
import { selectRaw as selectRawState, selectSharingIdProp } from './utils';
const formatInfosAPIReturn = data => ({
  outstandingAmount: data.outstanding_amount,
  annualBalance: data.annual_balance,
  monthlyInvoicingMode: Boolean(data.prepaid_monthly_invoice),
  grid: data.grid
});
export const mergeInfos = createAction('[redux-billing][infos] Merge infos');
export const resource = createResource('billing.billing.infos');
export const selectIsFetching = (state, {
  sharingId
}) => resource.selectIsFetchingOne(state, sharingId);
export const selectIsLoaded = (state, {
  sharingId
}) => resource.selectIsLoadedOne(state, sharingId);
export const selectIsFailure = (state, {
  sharingId
}) => resource.selectIsFailureOne(state, sharingId);
export const selectIsToRefetch = (state, {
  sharingId
}) => resource.selectIsToRefetchOne(state, sharingId);
export const selectShouldFetch = (state, {
  sharingId
}) => resource.selectShouldFetchOne(state, sharingId);
export const selectIsPatching = (state, {
  sharingId
}) => resource.selectIsPatchingOne(state, sharingId);
export const selectRaw = createSelector([selectRawState], raw => raw.infos);
const getInfos = (raw, sharingId) => raw[sharingId];
export const selectInfos = createSelector([selectRaw, selectSharingIdProp], getInfos);
export const selectInfosForCurrent = createSelector([selectRaw, selectCurrentId], getInfos);
const getGrid = (infos = {}) => infos.grid || 'A';
export const selectGrid = createSelector([selectInfos, selectSharingIdProp], getGrid);
export const selectGridForCurrent = createSelector([selectInfosForCurrent], getGrid);
const getMonthlyInvoicingMode = infos => infos && infos.monthlyInvoicingMode;
export const selectMonthlyInvoicingMode = createSelector([selectInfos, selectSharingIdProp], getMonthlyInvoicingMode);
export const selectMonthlyInvoicingModeForCurrent = createSelector([selectInfosForCurrent], getMonthlyInvoicingMode);
export const fetchInfos = (options = {}) => (dispatch, getState) => {
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  const invalidateMeans = require('./means').invalidateMeans;
  if (typeof sharingId !== 'string') {
    return Promise.reject(new Error('[billing.infos] `fetchInfos` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  return dispatch(resource.fetchOne(`/billing/infos/${sharingId}`, {
    ...options,
    label: sharingId,
    params: options.params || {},
    on200: response => {
      dispatch(invalidateMeans({
        sharingId
      }));
      return response;
    },
    not200: response => Promise.reject(response)
  }));
};
export const patchInfos = (params, options = {}) => (dispatch, getState) => {
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  if (typeof sharingId !== 'string') {
    return Promise.reject(new Error('[billing.infos] `patchInfos` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  const optimistic = params.optimistic || false;
  const body = {
    prepaid_monthly_invoice: params.monthlyInvoicingMode
  };
  const previousMonthlyInvoicingMode = selectMonthlyInvoicingMode(getState(), {
    sharingId
  });
  if (optimistic) {
    dispatch(mergeInfos({
      body: {
        monthlyInvoicingMode: params.monthlyInvoicingMode
      },
      sharingId
    }));
  }
  return dispatch(resource.patchOne(`/billing/infos/${sharingId}`, body, {
    ...options,
    label: sharingId,
    params: options.params || {},
    on200: response => response,
    not200: response => Promise.reject(response)
  })).catch(err => {
    if (optimistic) {
      dispatch(mergeInfos({
        body: {
          monthlyInvoicingMode: previousMonthlyInvoicingMode
        },
        sharingId
      }));
    }
    throw err;
  });
};
const reducer = createReducer({
  [resource.fetchOne.success.getType()]: (state, {
    response: {
      data
    },
    label: sharingId
  }) => ({
    ...state,
    [sharingId]: formatInfosAPIReturn(data)
  }),
  [mergeInfos]: (state, {
    body,
    sharingId
  }) => ({
    ...state,
    [sharingId]: {
      ...(state[sharingId] || {}),
      ...body
    }
  })
}, {});
export default reducer;